import React, { useState, useEffect, useCallback } from 'react';
import { Upload, message, Icon, Progress, Select, Button, Card, Collapse, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { getAllTeams } from '../../utils/services/teamsAPI';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { importXMLBlocks } from '../../utils/xmlBlockImportService';
import { exportBlocksToXML } from '../../utils/xmlBlockImporter';
import { BulkUploadButton } from '../TemplateSection/styles';
import './styles.css';

const { Panel } = Collapse;
const { Option } = Select;

const NAMESPACES = ['XML_BLOCK_IMPORT'];

const XMLBlockImportPage = () => {
  const { t } = useTranslation(NAMESPACES);
  const { userDetails } = React.useContext(AppStoreContext);
  
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [importProgress, setImportProgress] = useState(null);
  const [blockStatuses, setBlockStatuses] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [cancelImport, setCancelImport] = useState(false);

  // Fetch teams and categories on mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getAllTeams();
        if (response?.data) {
          setTeams(response.data);
          // Auto-select if only one team
          if (response.data.length === 1) {
            setSelectedTeamId(response.data[0].rgId);
          }
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        message.error('Failed to load teams');
      }
    };

    const fetchCategories = async () => {
      try {
        setFetchingCategories(true);
        const response = await getAllCategories(false, 'element');
        if (response?.statusCode === 507 && response?.data) {
          setCategories(response.data);
          // Auto-select if only one category
          if (response.data.length === 1) {
            setSelectedCategoryId(response.data[0].id);
          }
        } else {
          message.error('Failed to load categories');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        message.error('Failed to load categories');
      } finally {
        setFetchingCategories(false);
      }
    };
    
    fetchTeams();
    fetchCategories();
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (file) => {
    if (!selectedTeamId) {
      message.error('Please select a team first');
      return false;
    }

    if (!selectedCategoryId) {
      message.error('Please select a category first');
      return false;
    }

    if (!file.name.endsWith('.xml')) {
      message.error('Please upload an XML file');
      return false;
    }

    setLoading(true);
    setIsImporting(true);
    setCancelImport(false);
    setImportProgress(null);
    setBlockStatuses([]);
    setImportResult(null);

    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      // Progress callback
      const onProgress = (progressData) => {
        if (cancelImport) {
          return;
        }

        setImportProgress({
          stage: progressData.stage,
          message: progressData.message,
          currentBlock: progressData.currentBlock,
          currentLanguage: progressData.currentLanguage,
          blockIndex: progressData.blockIndex,
          totalBlocks: progressData.totalBlocks,
          processedBlocks: progressData.processedBlocks || 0,
          successfulImports: progressData.successfulImports || 0,
          failedImports: progressData.failedImports || 0,
        });

        // Update block statuses
        if (progressData.statusObj) {
          setBlockStatuses((prev) => {
            const existing = prev.findIndex(
              (s) => s.blockId === progressData.statusObj.blockId && s.language === progressData.statusObj.language
            );
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = { ...updated[existing], ...progressData.statusObj };
              return updated;
            }
            return [...prev, progressData.statusObj];
          });
        }
      };

      // Import blocks
      const result = await importXMLBlocks(fileContent, selectedTeamId, onProgress, selectedCategoryId);
      
      setImportResult(result);
      setIsImporting(false);
      setLoading(false);

      if (result.failedImports === 0) {
        message.success(`Successfully imported ${result.successfulImports} blocks`);
      } else {
        message.warning(
          `Import completed: ${result.successfulImports} successful, ${result.failedImports} failed`
        );
      }
    } catch (error) {
      console.error('Import error:', error);
      message.error(error.message || 'Failed to import XML file');
      setIsImporting(false);
      setLoading(false);
    }

    return false; // Prevent default upload behavior
  }, [selectedTeamId, selectedCategoryId, cancelImport]);

  const handleCancel = () => {
    setCancelImport(true);
    setIsImporting(false);
    setLoading(false);
    message.info('Import cancelled');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Icon type="loading" />;
      case 'success':
        return <Icon type="check-circle" style={{ color: '#52c41a' }} />;
      case 'error':
        return <Icon type="close-circle" style={{ color: '#ff4d4f' }} />;
      default:
        return <Icon type="clock-circle" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing':
        return 'processing';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPublishStatusIcon = (publishStatus) => {
    switch (publishStatus) {
      case 'processing':
        return <Icon type="loading" />;
      case 'qa-ready':
        return <Icon type="clock-circle" style={{ color: '#1890ff' }} />;
      case 'qa-complete':
        return <Icon type="check-circle" style={{ color: '#52c41a' }} />;
      case 'published':
        return <Icon type="check-circle" style={{ color: '#52c41a' }} />;
      case 'publish-error':
        return <Icon type="close-circle" style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  const getPublishStatusColor = (publishStatus) => {
    switch (publishStatus) {
      case 'processing':
        return 'processing';
      case 'qa-ready':
        return 'default';
      case 'qa-complete':
        return 'success';
      case 'published':
        return 'success';
      case 'publish-error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPublishStatusLabel = (publishStatus) => {
    switch (publishStatus) {
      case 'not-started':
        return 'Not Published';
      case 'processing':
        return 'Publishing...';
      case 'qa-ready':
        return 'QA Ready';
      case 'qa-complete':
        return 'QA Complete';
      case 'published':
        return 'Published';
      case 'publish-error':
        return 'Publish Error';
      default:
        return 'Unknown';
    }
  };

  const progressPercent = importProgress?.totalBlocks
    ? Math.round((importProgress.processedBlocks / importProgress.totalBlocks) * 100)
    : 0;

  // Group block statuses by block
  const groupedStatuses = blockStatuses.reduce((acc, status) => {
    const key = status.blockId || status.blockName || 'unknown';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(status);
    return acc;
  }, {});

  return (
    <div className="xml-block-import-page">
      <Card title="XML Block Import" style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Team:
          </label>
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Select a team"
            value={selectedTeamId}
            onChange={setSelectedTeamId}
            disabled={isImporting}
          >
            {teams.map((team) => (
              <Option key={team.rgId || team.id} value={team.rgId || team.id}>
                {team.groupName || team.name || `Team ${team.rgId || team.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Category: <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Select a category"
            value={selectedCategoryId}
            onChange={setSelectedCategoryId}
            loading={fetchingCategories}
            disabled={fetchingCategories || isImporting}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name}
              </Option>
            ))}
          </Select>
        </div>

        <Upload
          accept=".xml"
          showUploadList={false}
          beforeUpload={handleFileUpload}
          disabled={!selectedTeamId || !selectedCategoryId || isImporting}
        >
          <BulkUploadButton style={{ margin: 'auto', width: '50%' }}>
            <p className={loading ? 'downloadZip' : ''} style={{ visibility: loading ? 'visible' : 'hidden' }}>
              <Icon type="loading" style={{ marginRight: '5px', fontSize: '20px', color: '#FC6E51' }} />
            </p>
            <p className="import">
              {isImporting ? 'Importing...' : 'Upload XML File'}
            </p>
            <p className="desc">
              {isImporting
                ? 'Please wait while blocks are being imported'
                : 'Select an XML file to import blocks as standalone blocks'}
            </p>
          </BulkUploadButton>
        </Upload>

        {isImporting && (
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Button onClick={handleCancel} danger>
              Cancel Import
            </Button>
          </div>
        )}

        {importProgress && (
          <div style={{ marginTop: '30px' }}>
            <h3>Import Progress</h3>
            <Progress
              percent={progressPercent}
              status={importProgress.stage === 'error' ? 'exception' : 'active'}
              format={(percent) => `${percent}%`}
            />
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              {importProgress.message || (
                <>
                  {importProgress.stage === 'publishing' ? 'Publishing' : 'Processing'} block {importProgress.blockIndex || 0} of {importProgress.totalBlocks || 0}
                  {importProgress.currentBlock && (
                    <> - {importProgress.currentBlock} ({importProgress.currentLanguage})</>
                  )}
                  {importProgress.stage === 'publishing' && importProgress.statusObj?.publishStage && (
                    <> - {importProgress.statusObj.publishStage}</>
                  )}
                </>
              )}
            </div>
            {importProgress.totalBlocks && (
              <div style={{ marginTop: '10px' }}>
                <Tag color="green">Successful: {importProgress.successfulImports || 0}</Tag>
                <Tag color="red">Failed: {importProgress.failedImports || 0}</Tag>
                {importProgress.publishedBlocks !== undefined && (
                  <Tag color="blue">Published: {importProgress.publishedBlocks || 0}</Tag>
                )}
                {importProgress.publishErrors !== undefined && importProgress.publishErrors > 0 && (
                  <Tag color="orange">Publish Errors: {importProgress.publishErrors}</Tag>
                )}
                <Tag>Total: {importProgress.totalBlocks}</Tag>
              </div>
            )}
          </div>
        )}

        {blockStatuses.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <h3>Block Status</h3>
            <Collapse>
              {Object.entries(groupedStatuses).map(([blockId, statuses]) => (
                <Panel
                  key={blockId}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span>{blockId}</span>
                      {statuses.map((s) => (
                        <React.Fragment key={s.language}>
                          <Tag color={getStatusColor(s.status)}>
                            {s.language.toUpperCase()}: {s.status}
                          </Tag>
                          {s.publishStatus && s.publishStatus !== 'not-started' && (
                            <Tag color={getPublishStatusColor(s.publishStatus)}>
                              {getPublishStatusIcon(s.publishStatus)}
                              <span style={{ marginLeft: '4px' }}>
                                {getPublishStatusLabel(s.publishStatus)}
                              </span>
                            </Tag>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {statuses.map((status) => (
                      <div
                        key={`${status.blockId}_${status.language}`}
                        style={{
                          padding: '10px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {getStatusIcon(status.status)}
                          <Tag>{status.language.toUpperCase()}</Tag>
                          <span style={{ flex: 1 }}>
                            {status.status === 'error' && status.errorMessage
                              ? `Error: ${status.errorMessage}`
                              : status.status}
                          </span>
                        </div>
                        {status.publishStatus && status.publishStatus !== 'not-started' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '28px' }}>
                            {getPublishStatusIcon(status.publishStatus)}
                            <span style={{ fontSize: '13px', color: '#666' }}>
                              <strong>Publishing:</strong> {getPublishStatusLabel(status.publishStatus)}
                              {status.publishStage && status.publishStage !== status.publishStatus && (
                                <> ({status.publishStage})</>
                              )}
                            </span>
                            {status.publishErrorMessage && (
                              <Tag color="red" style={{ marginLeft: 'auto' }}>
                                {status.publishErrorMessage}
                              </Tag>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Panel>
              ))}
            </Collapse>
          </div>
        )}

        {importResult && !isImporting && (
          <div style={{ marginTop: '30px' }}>
            <h3>Import Summary</h3>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <strong>Total Blocks:</strong> {importResult.totalBlocks}
                </div>
                <div>
                  <strong>Processed:</strong> {importResult.processedBlocks}
                </div>
                <div>
                  <strong>Successful:</strong> <Tag color="green">{importResult.successfulImports}</Tag>
                </div>
                <div>
                  <strong>Failed:</strong> <Tag color="red">{importResult.failedImports}</Tag>
                </div>
                {importResult.publishedBlocks !== undefined && (
                  <div>
                    <strong>Published:</strong> <Tag color="blue">{importResult.publishedBlocks}</Tag>
                  </div>
                )}
                {importResult.qaReadyBlocks !== undefined && importResult.qaReadyBlocks > 0 && (
                  <div>
                    <strong>QA Ready:</strong> <Tag color="default">{importResult.qaReadyBlocks}</Tag>
                  </div>
                )}
                {importResult.qaCompleteBlocks !== undefined && importResult.qaCompleteBlocks > 0 && (
                  <div>
                    <strong>QA Complete:</strong> <Tag color="success">{importResult.qaCompleteBlocks}</Tag>
                  </div>
                )}
                {importResult.publishErrors !== undefined && importResult.publishErrors > 0 && (
                  <div>
                    <strong>Publishing Errors:</strong> <Tag color="orange">{importResult.publishErrors}</Tag>
                  </div>
                )}
                {importResult.errors.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <strong>Errors:</strong>
                    <ul style={{ marginTop: '5px' }}>
                      {importResult.errors.map((error, index) => (
                        <li key={index}>
                          {error.block} ({error.language}): {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {importResult.failedBlocks && importResult.failedBlocks.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        const xml = exportBlocksToXML(importResult.failedBlocks);
                        const blob = new Blob([xml], { type: 'application/xml' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'failed-blocks-export.xml';
                        a.click();
                        URL.revokeObjectURL(url);
                        message.success('Failed blocks exported as XML');
                      }}
                    >
                      Export failed blocks (XML)
                    </Button>
                    <span style={{ marginLeft: '8px', fontSize: '13px', color: '#666' }}>
                      {importResult.failedBlocks.length} block(s) – re-import this file after fixing issues
                    </span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
};

export default XMLBlockImportPage;

